import { useEffect, useState, useCallback } from "react";
import { Table, Input, message, Button, DatePicker } from "antd";
import { PlusOutlined } from "@ant-design/icons"; // {{ edit_1 }}
import axios from "axios";
import moment from "moment";
import { useFetchData } from "../context/FetchDataContext";
import EditModal from "./EditForm";
import CreateForm from "./CreateForm";
import Helper from "../utils/helper";
import { ENDPOINTS } from "../constants/api";

const ListPage = () => {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { triggerRefetch, setTriggerRefetch, setUpdateTableDataFunction } =
    useFetchData();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createdAtEnd, setCreatedAtEnd] = useState(null); // New state for end date
  const [createdAtStart, setCreatedAtStart] = useState(null);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const authToken = Helper.getTokenFromLocalStorage();
      const authorizationHeader = authToken ? `${authToken}` : "";
      const dealerCode = localStorage.getItem("dealer_code");

      const customerResult = await axios.get(
        ENDPOINTS.GET_CUSTOMERS_BY_DEALER_CODE,
        {
          headers: {
            Authorization: authorizationHeader,
          },
          params: {
            dealer_code: dealerCode,
          },
        }
      );

      setData(customerResult.data.customers || []);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      if (error.response && error.response.status === 401) {
        message.error("Session expired, redirecting to login page.");
        setTimeout(() => {
          Helper.handleLogout();
        }, 3000);
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationshipManagers = async () => {
    try {
      const authToken = Helper.getTokenFromLocalStorage();
      const authorizationHeader = authToken ? `${authToken}` : "";
      const dealerCode = localStorage.getItem("dealer_code");

      const rmResult = await axios.get(ENDPOINTS.GET_RELATIONSHIP_MANAGERS, {
        headers: {
          Authorization: authorizationHeader,
        },
        params: {
          dealer_code: dealerCode,
        },
      });

      localStorage.setItem(
        "relationshipManagers",
        JSON.stringify(rmResult.data)
      );
    } catch (error) {
      console.error("Failed to fetch relationship managers:", error);
    }
  };
  const formatDateC = (dateString) => {
    const date = new Date(dateString);

    return moment(date).format("h:mm A, Do MMMM YYYY"); // Format date as "23rd August 2024"
  };

  useEffect(() => {
    // {{ edit_1 }}
    const fetchData = async () => {
      await fetchCustomers();
      await fetchRelationshipManagers();
      setTriggerRefetch(false);
      // Clear temporary customers when fetching new data
    };

    fetchData(); // Initial fetch
    const intervalId = setInterval(fetchData, 60000); // Fetch data every minute

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [triggerRefetch, setTriggerRefetch]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return moment(date).format("YYYY-MM-DDTHH:mm:ss"); // Format date as needed
  };

  const downloadCSV = () => {
    // {{ edit_1 }}
    const csvContent = [
      // CSV header
      [
        "Serial No.",
        "Lead ID",
        "Name",
        "Preferred Model",
        "Phone Number",
        "SM Name",
        "Created At",
      ],
      // CSV data
      ...data.map((item, index) => [
        index + 1,
        item.LeadID,
        `${item.FirstName} ${item.LastName}`,
        item.PreferredModel,
        item.PhoneNumber,
        item.RMName,
        formatDate(item.CreatedAt), // Format date as needed
      ]),
    ]
      .map((row) => row.join(",")) // Join each row with commas
      .join("\n"); // Join rows with new lines

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "customers.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Lead ID",
      dataIndex: "LeadID",
      key: "leadId",
      sorter: (a, b) => a.LeadID.localeCompare(b.LeadID),
      render: (text, _, i) =>
        text === "Missing Lead Id" || text === "" ? "Pending" : text, // {{ edit_1 }}
    },
    {
      title: "Name",
      key: "name",
      render: (text, record) => `${record.FirstName} ${record.LastName}`,
      sorter: (a, b) => a.FirstName.localeCompare(b.FirstName),
    },
    {
      title: "Car Model",
      dataIndex: "PreferredModel",
      key: "preferredModel",
      sorter: (a, b) => a.PreferredModel.localeCompare(b.PreferredModel),
      render: (text) => {
        if (text === "Model_H") return "Windsor";
        if (text === "WINDSOR_PRO") return "Windsor Pro";
        return text;
      }, // Updated to handle WINDSOR_PRO
    },
    {
      title: "Phone Number", // {{ edit_1 }}
      dataIndex: "PhoneNumber", // Ensure this matches your data structure
      key: "phoneNumber",
      sorter: (a, b) => a.PhoneNumber.localeCompare(b.PhoneNumber),
    },
    {
      title: "SM Name",
      dataIndex: "RMName",
      key: "rmName",
      sorter: (a, b) => a.RMName.localeCompare(b.RMName),
    },
    {
      title: "Creation Date",
      dataIndex: "CreatedAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt),
      render: formatDateC,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            onChange={(dates) => {
              setSelectedKeys(
                dates
                  ? [
                      dates[0].format("YYYY-MM-DD"),
                      dates[1].format("YYYY-MM-DD"),
                    ]
                  : []
              );
              setCreatedAtStart(dates ? dates[0] : null); // Set start date
              setCreatedAtEnd(dates ? dates[1] : null); // Set end date
            }}
            onOk={() => confirm()}
            value={[createdAtStart, createdAtEnd]} // Use the new state values
            style={{ marginBottom: 8, display: "flex", padding: 5 }}
          />
          <Button
            onClick={() => {
              clearFilters();
              setCreatedAtStart(null);
              setCreatedAtEnd(null);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => {
        const createdAt = new Date(record.created_at).setHours(0, 0, 0, 0); // Set time to midnight
        const start = createdAtStart
          ? new Date(createdAtStart).setHours(0, 0, 0, 0)
          : null; // Set time to midnight
        const end = createdAtEnd
          ? new Date(createdAtEnd).setHours(0, 0, 0, 0)
          : null; // Set time to midnight
        return !start || !end || (createdAt >= start && createdAt <= end); // Check if created_at is within the range
      },
    },
  ];

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const updateTableData = useCallback((newCustomer) => {
    setData((prevData) => [newCustomer, ...prevData]);
  }, []);

  useEffect(() => {
    setUpdateTableDataFunction(updateTableData);
  }, [setUpdateTableDataFunction, updateTableData]);

  const getFilteredData = () => {
    return data.filter((item) => {
      const createdAt = new Date(item.CreatedAt).setHours(0, 0, 0, 0);
      const startCreatedAt = createdAtStart
        ? new Date(createdAtStart).setHours(0, 0, 0, 0)
        : null;
      const endCreatedAt = createdAtEnd
        ? new Date(createdAtEnd).setHours(0, 0, 0, 0)
        : null;
      const matchesCreatedAt =
        (!startCreatedAt || createdAt >= startCreatedAt) &&
        (!endCreatedAt || createdAt <= endCreatedAt);

      return (
        matchesCreatedAt &&
        (`${item.FirstName} ${item.LastName}`
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
          item.LeadID?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.PreferredModel?.toLowerCase().includes(
            searchText.toLowerCase()
          ) ||
          item.PhoneNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.RMName?.toLowerCase().includes(searchText.toLowerCase()))
      );
    });
  };

  const filteredData = getFilteredData();
  return (
    <div
      style={{
        width: "90%",
        margin: "auto",
        height: "700px",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          marginTop: 8,
          marginBottom: 8,
        }}
      >
        <Input.Search
          placeholder="Search"
          onSearch={(value) => setSearchText(value)}
          style={{
            marginBottom: 16,
            marginTop: 16,
            marginRight: 4,
            width: "70%",
          }}
        />
        <Button onClick={downloadCSV} type="default">
          Export
        </Button>
      </div>
      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="ID"
        pagination={{
          pageSize: 8,
          position: ["topRight"],
        }}
        defaultSortOrder={["desc"]}
        onChange={() => {}}
        loading={loading}
        locale={{
          emptyText: "No customers found",
        }}
      />
      {isEditModalVisible && (
        <EditModal
          isVisible={isEditModalVisible}
          onClose={() => setIsEditModalVisible(false)}
        />
      )}
      {isCreateModalVisible && (
        <CreateForm
          isVisible={isCreateModalVisible}
          onClose={() => setIsCreateModalVisible(false)}
          updateTableData={updateTableData}
        />
      )}

      {isMobile && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => setIsCreateModalVisible(true)}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1000,
          }}
        />
      )}
    </div>
  );
};

export default ListPage;
